/* eslint-disable no-undefined */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import FilePreviewHeader from './file-preview-header';
import FilePreviewDisplay from './file-preview-display';
import { closeFilePreview } from '@actions/attachment-actions';
import './file-preview.scss';

const FilePreview = () => {
  const dispatch = useDispatch();

  // Redux state
  const index = useSelector(state => state.attachments.index ?? -1);
  const attachments = useSelector(state => {
    const { attachments: allAttachments, contentType: ct, objectId: oid } = state.attachments;
    return allAttachments[ct + oid] || [];
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef(null);
  const triggerElementRef = useRef(null);
  const isOpen = index >= 0 && attachments.length > 0;

  // Sync currentIndex when index prop changes from negative to positive
  useEffect(() => {
    if (index >= 0) {
      setCurrentIndex(index);
    }
  }, [index]);

  const closePreview = useCallback(() => {
    dispatch(closeFilePreview());
  }, [dispatch]);

  // This component is always mounted (rendered unconditionally wherever a page needs it) and
  // only toggles its own content via isOpen, so a mount-once effect would never fire again after
  // the first open - key it off isOpen instead, same fix as the M4 layers-menu popover.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    triggerElementRef.current = document.activeElement;
    containerRef.current?.focus();
    return () => {
      const trigger = triggerElementRef.current;
      if (trigger && typeof trigger.focus === 'function' && document.contains(trigger)) {
        trigger.focus();
      }
    };
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleKeyDown = key => {
      switch (key.code) {
      case 'ArrowLeft':
        setCurrentIndex(prev =>
          prev === 0 ? attachments.length - 1 : prev - 1
        );
        break;
      case 'ArrowRight':
        setCurrentIndex(prev =>
          prev === attachments.length - 1 ? 0 : prev + 1
        );
        break;
      case 'Escape':
        closePreview();
        break;
      default:
        // do nothing
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [attachments.length, isOpen, closePreview]);

  const getCurrentAsset = useCallback(() => {
    if (attachments && attachments.length > currentIndex) {
      return attachments[currentIndex];
    }
    return null;
  }, [attachments, currentIndex]);

  if (!isOpen) {
    return null;
  }

  const asset = getCurrentAsset();

  if (!asset) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-label={asset.file_name}
      tabIndex={-1}
      styleName="file-preview-container"
    >
      <FilePreviewHeader
        fileName={asset.file_name}
        total={attachments.length}
        current={currentIndex + 1}
        assetUrl={asset.signed_url}
        downloadUrl={asset.signed_download_url}
        closePreview={closePreview}
      />
      <FilePreviewDisplay asset={asset} />
    </div>
  );
};

export default FilePreview;
